"use client";

import { useCallback, useEffect } from "react";

import { useAddToCartSlider } from "@/app/hooks/useAddToCartSlider";
import { useAddToCartErrorStore } from "@/app/store";
import {
	deriveAddToCartState,
	isCartDisabledByState,
	type AddToCartButtonState
} from "@/composites/cart/AddToCartButton";
import {
	ProductCard,
	selectHighlightTags,
	type ProductCardProduct
} from "@/composites/product";
import type {
	ListingProduct,
	ListingProductRating
} from "@/domain/product-listing";

const ADD_TO_CART_LABEL = "Add to Cart";
const ADDING_LABEL = "Adding…";
const QUANTITY = 1;

export interface PlpProductGridItemProps {
	product: ListingProduct;
	rating?: ListingProductRating;
}

/**
 * Its own component so `useAddToCartSlider` is called once per card — the same
 * cart path the PDP and the upsell card use, with no listing-specific contract.
 */
export function PlpProductGridItem({
	product,
	rating
}: PlpProductGridItemProps): JSX.Element {
	const clearError = useAddToCartErrorStore((state) => state.clear);
	const failCart = useAddToCartErrorStore((state) => state.fail);

	const salePrice = product.price?.display ?? 0;
	const { onClick, state } = useAddToCartSlider({
		sku: product.sku,
		quantity: QUANTITY,
		source: "plp",
		product: {
			sku: product.sku,
			quantity: QUANTITY,
			name: product.title,
			imageUrl: product.image.src,
			listPrice: product.price?.strikeThrough ?? salePrice,
			salePrice
		}
	});

	useEffect(() => {
		if (state.errorStatus && state.error) {
			failCart(state.errorStatus, state.error);
		}
	}, [state.errorStatus, state.error, failCart]);

	const handleAddToCart = useCallback(() => {
		clearError();
		onClick();
	}, [clearError, onClick]);

	const cartState = toCartState(product);
	const isUnbuyable = isCartDisabledByState(cartState.kind);

	return (
		<ProductCard
			product={toCardProduct(product, rating)}
			action={{
				label: state.loading ? ADDING_LABEL : ADD_TO_CART_LABEL,
				reserveLabel: ADD_TO_CART_LABEL,
				isDisabled: isUnbuyable,
				onPress:
					isUnbuyable || state.loading ? undefined : handleAddToCart
			}}
		/>
	);
}

// A record the projection left priceless would add at 0.00, so it takes the
// existing "unavailable" treatment rather than a state of its own.
function toCartState(product: ListingProduct): AddToCartButtonState {
	if (!product.price) return { kind: "temporarily-unavailable" };

	return deriveAddToCartState(product);
}

function toCardProduct(
	product: ListingProduct,
	rating?: ListingProductRating
): ProductCardProduct {
	return {
		sku: product.sku,
		title: product.title,
		href: product.href,
		image: product.image,
		price: product.price,
		rating: rating ?? product.rating,
		tags: selectHighlightTags(
			product.highlightTag ? [product.highlightTag] : []
		)
	};
}
